import React, { useEffect, useMemo, useRef } from 'react';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import renderField from '../../../shared/components/custom/Field';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import validateSubscriptionPlanForm from './validateSubscriptionPlanForm';
import renderSwitchField from '../../../shared/components/custom/Switch';
import { partial } from '../../../util/translation/translation';
import { getImageHash } from '../../../util/functions';
import TextEditor2 from '../../../shared/components/text-editor/TextEditor2';
import renderSelectField from '../../../shared/components/form/Select';
import useConfig from '../../../util/useConfig';
import TimetableIcon from 'mdi-react/TimetableIcon';
import moment from 'moment';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import PopularAmountsField from './PopularAmountsField';
import { connect } from 'react-redux';
import clubConfig from '../../../config';
import FormCard from '../../../shared/components/custom/FormCard';
import PlanMilestonesPage from './PlanMilestonesPage';
import PlanConnectCard from './PlanConnectCard';
import { isWholeKrPlan, minCapMultiplier } from '../amountRules';

const SubscriptionPlanForm = ({
  subscriptionPlan,
  handleSubmit,
  initialize,
  change,
  dirty,
  type,
  subscriptionPrizes,
  formValues,
}) => {
  const config = useConfig();
  const s = partial('shared');
  const p = partial('goalSponsor');
  const prizesOptions = useMemo(() => {
    return subscriptionPrizes.map((prize) => ({
      label: prize.title,
      value: prize.id,
    }));
  }, [subscriptionPrizes]);
  const vatOptions = [
    { label: 0, value: 0 },
    { label: clubConfig.vat, value: clubConfig.vat },
  ];
  const scoreTypeOptions = useMemo(
    () => [
      { value: 'goal', label: p('scoreTypeGoal') },
      { value: 'point', label: p('scoreTypePoint') },
      { value: 'attendant', label: p('scoreTypeAttendant') },
      { value: 'holeInOne', label: p('scoreTypeHoleInOne') },
      { value: 'medal', label: p('scoreTypeMedal') },
    ],
    []
  );

  const clubVat = clubConfig.vat || 0;
  useEffect(() => {
    if (subscriptionPlan) {
      initialize({
        name: subscriptionPlan.name,
        subtitle: subscriptionPlan.subtitle,
        description: subscriptionPlan.description,
        isActive: !!subscriptionPlan.isActive,
        headingImage: {
          name: 'subscription plan image',
          preview: subscriptionPlan.imageUrl,
          croppedImage: subscriptionPlan.imageUrl,
          hash: getImageHash(subscriptionPlan.imageUrl),
        },
        prizes: subscriptionPlan.planPrizes.map((planPrize) => ({
          label: planPrize.prize.title,
          value: planPrize.prize.id,
        })),
        endDate: moment(subscriptionPlan.endDate).format('YYYY-MM-DDTHH:mm'),
        planDesiredValue: subscriptionPlan.planDesiredValue,
        dynamicLink: subscriptionPlan.dynamicLink,
        goal1: subscriptionPlan.goalCapPerGame?.[0] || 1,
        goal2: subscriptionPlan.goalCapPerGame?.[1] || 1,
        goal3: subscriptionPlan.goalCapPerGame?.[2] || 1,
        goalCapPerGameStep: subscriptionPlan.goalCapPerGameStep ?? 5,
        showPriceMeter: subscriptionPlan.showPriceMeter,
        scoreType:
          scoreTypeOptions.find(
            (option) => option.value === subscriptionPlan.scoreType
          ) || scoreTypeOptions[0],
        externalInvoiceCompanies:
          subscriptionPlan.externalInvoiceCompanies || false,
        // Plans made before the switch (not set) offer Swish
        swishEnabled: subscriptionPlan.swishEnabled !== false,
        vat: vatOptions.find(
          (option) =>
            option.value === subscriptionPlan.defaultPaymentOption?.vat
        ),
        popularAmounts: subscriptionPlan.popularAmounts?.length
          ? subscriptionPlan.popularAmounts
          : [10, 20, 50],
        amountStep: subscriptionPlan.amountStep ?? 5,
        amountMin: subscriptionPlan.amountMin ?? 5,
      });
    } else {
      initialize({
        scoreType: scoreTypeOptions[0],
        externalInvoiceCompanies: false,
        // New plans start without Swish; the club turns it on
        swishEnabled: false,
        goalCapPerGameStep: 5,
        vat: vatOptions.find((option) => option.value === clubVat),
        popularAmounts: [10, 20, 50],
        amountStep: 5,
        amountMin: 5,
      });
    }
  }, [subscriptionPlan]);

  // Autofill the max-limit quick options from the matching popular amount
  // (x5) whenever that specific amount actually changes - only once the
  // form is dirty, so loading saved/initial values never overwrites them.
  // The goal fields stay freely editable afterwards; they just won't keep
  // following the popular amount unless it's edited again.
  // Plans billed in whole kr take amounts under 1 kr; their max per game
  // (quick option times the amount) is at least 5 kr
  const wholeKr = isWholeKrPlan(subscriptionPlan);
  const amountStepAttr = wholeKr ? 'any' : undefined;
  const popularAmountsKey = JSON.stringify(formValues?.popularAmounts);
  const prevPopularAmountsRef = useRef(formValues?.popularAmounts);
  useEffect(() => {
    const current = formValues?.popularAmounts || [];
    const prev = prevPopularAmountsRef.current || [];
    if (dirty) {
      ['goal1', 'goal2', 'goal3'].forEach((fieldName, index) => {
        const value = current[index];
        if (
          value !== undefined &&
          value !== '' &&
          Number(value) !== Number(prev[index])
        ) {
          const multiplier = Number(value) * 5;
          const amountMin = Number(formValues?.amountMin);
          change(
            fieldName,
            wholeKr && amountMin > 0
              ? Math.max(Math.ceil(multiplier), minCapMultiplier(amountMin))
              : multiplier
          );
        }
      });
    }
    prevPopularAmountsRef.current = current;
  }, [popularAmountsKey]);

  // Same idea for the max-limit step, mirroring the amount step (x1).
  const prevAmountStepRef = useRef(formValues?.amountStep);
  useEffect(() => {
    const current = formValues?.amountStep;
    if (
      dirty &&
      current !== undefined &&
      current !== '' &&
      Number(current) !== Number(prevAmountStepRef.current)
    ) {
      change('goalCapPerGameStep', Number(current));
    }
    prevAmountStepRef.current = current;
  }, [formValues?.amountStep]);
  const scoreLabel = formValues?.scoreType?.label || scoreTypeOptions[0].label;
  return (
    <form className="form" onSubmit={handleSubmit}>
      <Row className="mb-4">
        <Col md={6} className="mb-4 mb-md-0">
          <FormCard title={s('details')}>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{s('name')}*</span>
                  <Field
                    name="name"
                    component={renderField}
                    placeholder={s('name')}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group flex-Vcenter">
                  <span className="form-header" />
                  <Field
                    name="isActive"
                    component={renderSwitchField}
                    label={[`${s('active')}`, `${s('inActive')}`]}
                    defaultChecked
                    className="colored-click"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">{s('subtitle')}*</span>
                  <Field
                    name="subtitle"
                    component={renderField}
                    placeholder={s('subtitle')}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form">{p('planEndDate')}*</span>
                  <div className="form__form-group-field">
                    <div className="form__form-group-icon">
                      <TimetableIcon />
                    </div>
                    <Field
                      name="endDate"
                      component={renderField}
                      type="datetime-local"
                    />
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="form__form-group">
                  <span className="form-header">{s('description')}</span>
                  <div className="marginTop-5" />
                  <div className="form__form-group form__form-group-id">
                    <Field name="description" component={TextEditor2} />
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{p('scoreType')}*</span>
                  <Field
                    name="scoreType"
                    placeholder={p('selectScoreType')}
                    options={scoreTypeOptions}
                    component={renderSelectField}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">{p('selectPrizes')}</span>
                  <Field
                    name="prizes"
                    placeholder={p('selectPrizes')}
                    options={prizesOptions}
                    component={renderSelectField}
                    multiple
                  />
                </div>
              </Col>
            </Row>
            {subscriptionPlan ? (
              <Row>
                <Col>
                  <div className="form__form-group">
                    <span className="form-header marginRight-8">
                      {s('universalLink')}
                    </span>
                    <div className="form__form-group-field">
                      <Field name="dynamicLink" component="input" disabled />
                      <button
                        type="button"
                        className="form__form-group-icon"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            subscriptionPlan.dynamicLink
                          )
                        }
                      >
                        <LinkVariantIcon />
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>
            ) : null}
          </FormCard>
        </Col>
        <Col md={6}>
          <div className="mb-4">
            <FormCard title={s('image')}>
              <Row>
                <Col md={12}>
                  <div className="form__form-group">
                    <Field
                      saveBase64Cropped
                      name="headingImage"
                      maxSize={3}
                      component={renderDropZoneField}
                      displayCrop
                      crop="BANNER_CROP"
                    />
                    <span className="form-header">
                      <strong>{s('note')}:</strong> {s('recommendedImageSize')}{' '}
                      16:9 (1035 x 582 px)
                    </span>
                  </div>
                </Col>
              </Row>
            </FormCard>
          </div>
          <FormCard title={p('fundraisingGoal')}>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">
                    {p('planDesiredValue')}* &nbsp;
                    <TooltipWithIcon
                      id="plan-desired-value-tooltip"
                      text={p('planDesiredValueDescription')}
                    />
                  </span>
                  <div className="form__form-group-field">
                    <Field
                      name="planDesiredValue"
                      component={renderField}
                      type="number"
                      disabled={!formValues?.showPriceMeter}
                      placeholder={p('planDesiredValue')}
                      format={(value) =>
                        formValues?.showPriceMeter ? value : ''
                      }
                    />
                    <div className="form__form-group-icon">
                      &nbsp;{config.currencyCaps}&nbsp;
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <hr />
            <Row>
              <Col md={12}>
                <div className="form__form-group flex-Vcenter">
                  <span className="form-header">
                    {p('progressBar')} &nbsp;
                    <TooltipWithIcon
                      id="progress-bar-tooltip"
                      text={p('progressBarDescription')}
                    />
                  </span>
                  <div className="marginLeft-8">
                    <Field
                      name="showPriceMeter"
                      component={renderSwitchField}
                      label={[p('showPriceMeter'), p('hidePriceMeter')]}
                      className="colored-click"
                      defaultChecked
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </FormCard>
        </Col>
      </Row>
      {subscriptionPlan ? (
        <Row className="mb-4">
          <Col md={12}>
            <FormCard title={p('milestones')}>
              <PlanMilestonesPage planId={subscriptionPlan.id} />
            </FormCard>
          </Col>
        </Row>
      ) : null}
      <Row className="mb-4">
        <Col md={12}>
          <FormCard title={p('payment')}>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">
                    {s('vat')}* &nbsp;
                    <TooltipWithIcon
                      id="vat-tooltip"
                      text={p('vatDescription')}
                    />
                  </span>
                  <div className="form__form-group-field">
                    <Field
                      name="vat"
                      placeholder={s('vat')}
                      options={vatOptions}
                      component={renderSelectField}
                      disabled={subscriptionPlan}
                    />
                    <div className="form__form-group-icon">&nbsp;%&nbsp;</div>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <PopularAmountsField
                  currency={config.currencyCaps}
                  step={amountStepAttr}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">
                    {p('amountStep')} &nbsp;
                    <TooltipWithIcon
                      id="amount-step-tooltip"
                      text={p('amountStepDescription')}
                    />
                  </span>
                  <div className="form__form-group-field">
                    <Field
                      name="amountStep"
                      component={renderField}
                      type="number"
                      min={1}
                      placeholder={p('amountStep')}
                    />
                    <div className="form__form-group-icon">
                      &nbsp;{config.currencyCaps}&nbsp;
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">
                    {p('amountMin')} &nbsp;
                    <TooltipWithIcon
                      id="amount-min-tooltip"
                      text={p('amountMinDescription')}
                    />
                  </span>
                  <div className="form__form-group-field">
                    <Field
                      name="amountMin"
                      component={renderField}
                      type="number"
                      min={1}
                      step={amountStepAttr}
                      placeholder={p('amountMin')}
                    />
                    <div className="form__form-group-icon">
                      &nbsp;{config.currencyCaps}&nbsp;
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <hr />
            <Row>
              <Col md={12}>
                <div className="form__form-group flex-Vcenter">
                  <span className="form-header">
                    {p('externalInvoiceCompanies')} &nbsp;
                    <TooltipWithIcon
                      id="external-invoice-companies-tooltip"
                      text={p('externalInvoiceCompaniesDescription')}
                    />
                  </span>
                  <div className="marginLeft-8">
                    <Field
                      name="externalInvoiceCompanies"
                      component={renderSwitchField}
                      label={[s('active'), s('inActive')]}
                      className="colored-click"
                    />
                  </div>
                </div>
              </Col>
            </Row>
            {/* Swish is SEK only, so only Swedish clubs choose */}
            {config.currencyCaps === 'SEK' && (
              <Row>
                <Col md={12}>
                  <div className="form__form-group flex-Vcenter">
                    <span className="form-header">
                      {p('swishPayments')} &nbsp;
                      <TooltipWithIcon
                        id="swish-payments-tooltip"
                        text={p('swishPaymentsDescription')}
                      />
                    </span>
                    <div className="marginLeft-8">
                      <Field
                        name="swishEnabled"
                        component={renderSwitchField}
                        label={[s('active'), s('inActive')]}
                        className="colored-click"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </FormCard>
        </Col>
      </Row>
      {subscriptionPlan?.defaultPaymentOption?.id ? (
        <PlanConnectCard
          paymentOption={subscriptionPlan.defaultPaymentOption}
        />
      ) : null}
      <Row className="mb-4">
        <Col md={12}>
          <FormCard
            title={
              <>
                {p('goalCapPerGame')} &nbsp;
                <TooltipWithIcon
                  id="no-access-view-tooltip"
                  text={p('goalCapPerGameInfo')}
                />
              </>
            }
          >
            <Row>
              <Col>
                <p>{`${p('goalCapDescription')}`}</p>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div className="form__form-group">
                  <span className="form-header">{p('goalsLabel')} 1*</span>
                  <Field
                    name="goal1"
                    component={renderField}
                    placeholder={`${p('numberOfScorePlaceholder', { score: scoreLabel })}1`}
                    type="number"
                    min={1}
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="form__form-group">
                  <span className="form-header">{p('goalsLabel')} 2*</span>
                  <Field
                    name="goal2"
                    component={renderField}
                    placeholder={`${p('numberOfScorePlaceholder', { score: scoreLabel })}2`}
                    type="number"
                    min={1}
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="form__form-group">
                  <span className="form-header">{p('goalsLabel')} 3*</span>
                  <Field
                    name="goal3"
                    component={renderField}
                    placeholder={`${p('numberOfScorePlaceholder', { score: scoreLabel })}3`}
                    type="number"
                    min={1}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div className="form__form-group">
                  <span className="form-header">
                    {p('goalCapPerGameStep')} &nbsp;
                    <TooltipWithIcon
                      id="goal-cap-per-game-step-tooltip"
                      text={p('goalCapPerGameStepDescription')}
                    />
                  </span>
                  <Field
                    name="goalCapPerGameStep"
                    component={renderField}
                    type="number"
                    min={1}
                    placeholder={p('goalCapPerGameStep')}
                  />
                  <small className="text-muted">
                    {p('goalCapPerGameStepRecommendation')}
                  </small>
                </div>
              </Col>
            </Row>
          </FormCard>
        </Col>
      </Row>
      <Col>
        <Button color="primary">
          {type === 'create' ? `${s('create')}` : `${s('save')}`}
        </Button>
        <Link to={`/goal-sponsor`}>
          <Button>{s('close')}</Button>
        </Link>
      </Col>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'subscription_plan_form',
  validate: validateSubscriptionPlanForm,
});

const mapStateToProps = (state) => ({
  formValues: getFormValues('subscription_plan_form')(state),
});

export default connect(mapStateToProps)(reduxF(SubscriptionPlanForm));
